/** @jsxImportSource @emotion/react */
import downArrow from '../../assets/icons/downArrowSolidBlack.svg';
import * as React from 'react';
import { ColumnConfig } from './columnConfig';
import { css, SerializedStyles } from '@emotion/react';
import { Loader } from '../../forms/elements/loader/Loader';
import { isNilOrEmpty } from '../../language/module';
import * as R from 'ramda';
import { MediumText, UNSELECTABLE_TEXT } from '../../styles/styles';
import { COOL_BLUE, COOLER_GREY, LIGHT_GREY, WHITE } from '../../styles/colors';
import ErrorView from '../../forms/ErrorView';
import { TooltipFC } from '../TooltipFC';
import infoImg from '../../assets/icons/info.svg';
import { BoxedMessageStyle } from '../../forms/elements/boxedMessage/boxedMessageStyle';

const rowItemStyles = css`
  padding: 10px;  
  line-height: 22px;
  border: 1px solid transparent;
`;


const tableStyle = css`
  border-spacing: 0;
  width: 100%;
  transition: all .5s ease;

  thead tr {
    ${MediumText};
    align-items: center;
  }

  tbody tr {
    ${MediumText};
    border-color: ${COOLER_GREY};
    background-color: ${WHITE};

    &:nth-of-type(odd) {
      background-color: ${LIGHT_GREY};
    }

    &:last-of-type {
      td {
        border-bottom: 1px solid ${COOLER_GREY};
      }
    }

    &:hover {
      background-color: ${COOL_BLUE};
    }
  }

  th {
    ${rowItemStyles};
    padding-top: 0;
    border-bottom: 0;
    text-align: left;
  }

  td {
    ${rowItemStyles};
    border-color: ${COOLER_GREY};
    border-right: 0;
    border-bottom: 0;
    height: 100%;

    &:last-of-type {
      border-right: 1px solid ${COOLER_GREY};
    }
  }
`;

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-bottom: -3px; /* align image with text */
  margin-left: 5px;
  pointer-events: initial;
  font-size: 16px;
`;

const sortArrowCss = (isVisible, sortDirection) => css`
  display: inline-block;
  visibility: ${isVisible ? 'visible' : 'hidden'};
  transform: ${sortDirection === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: visibility 10ms linear;
  vertical-align: top;
  cursor: pointer;
  flex-shrink: 0; /* Prevent arrow from shrinking when space is tight */
`;

type SortDirection = 'asc' | 'desc';


const invert = (direction: SortDirection): SortDirection => direction === 'asc' ? 'desc' : 'asc';


/**
 * Table: a component that takes some data, some information about that data, and renders a table whose rows can be
 * sorted. Individual columns can be marked sortable/non-sortable, a default sort column can be indicated, data can be
 * changed and subsequently re-rendered. The table has options for a no-data message, and loading state.
 *
 * ***NOTE***: If the node's root returned by renderRow is not a <tr>, things will BREAK!
 *
 * TODO: Ensure table properly animates size changes
 * TODO: Consider offering some kind of error messaging option -- it would be nice if we could nicely show errors in the table
 * TODO: Set min row count such that spinner height = min row count height (avoid chunky transition to loaded)
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class Table<TData> extends React.PureComponent<{
  columns: Array<ColumnConfig<TData>>;
  data: TData[];
  noDataMessage: string;
  renderRow: (model: TData, index: number) => React.ReactNode;
  isLoading: boolean;
  error?: string;
  errorBoxedStyle?: BoxedMessageStyle;
  rootCss?: SerializedStyles;
  infoMsg?: string;
  infoKey?: string;
}, {
  sortDirection: SortDirection;
  sortColumn: ColumnConfig<TData> | undefined;
  sortedData: TData[];
}> {
  constructor(props) {
    super(props);

    // Set initial component state with initial data sorted by default sorting column
    const defaultColumn                   = R.find(
      (column: ColumnConfig<TData>) => !!column.defaultSortingColumn,
      props.columns || [],
    );
    const defaultDirection: SortDirection = defaultColumn ? defaultColumn.defaultSortingDirection || 'asc' : 'asc';

    this.state = this.sortData(
      defaultColumn,
      {
        sortColumn:    defaultColumn,
        sortDirection: invert(defaultDirection),  // Inverting because sortData will invert our default direction
        sortedData:    props.data || [],
      },
    );
  }


  public componentDidUpdate(prevProps): void {
    const columnsChanged = !R.equals(prevProps.columns, this.props.columns);
    const dataChanged    = !R.equals(prevProps.data, this.props.data);

    if (!columnsChanged && !dataChanged) {
      return;
    }

    const { columns, data } = this.props;
    const { sortColumn, sortDirection } = this.state;

    const defaultSortColumn = R.find<ColumnConfig<TData>>(
      column => !!column.defaultSortingColumn,
      columns || [],
    );

    const currentSortColumn = R.find<ColumnConfig<TData>>(
      column =>
        !!sortColumn &&
        (column === sortColumn ||
          (column.id !== undefined && column.id === sortColumn.id)),
      columns || [],
    );

    const sortingColumn = currentSortColumn || defaultSortColumn;

    // No valid sort column → reset sorting
    if (!sortingColumn || !sortingColumn.onSort) {
      this.setState({
        sortDirection: 'asc',
        sortColumn: undefined,
        sortedData: data || [],
      });
      return;
    }

    // Preserve direction only if the column survived
    const direction = currentSortColumn ? sortDirection : 'asc';

    this.setState({
      sortDirection: direction,
      sortColumn: sortingColumn,
      sortedData: sortingColumn.onSort(direction === 'asc', data || []),
    });
  }


  private onSort = (
    column: ColumnConfig<TData> | undefined,
  ) => () => this.setState(prev => this.sortData(column, prev));


  /**
   * Map old state and a target sorting column into new state. Pure; does not change component state.
   */
  private sortData = (
    column: ColumnConfig<TData> | undefined,
    oldState,
  ) => {
    // If we can't sort by this column, do nothing
    if (!column || !column.onSort) {
      return oldState;
    }

    // Invert direction if this column is already sorting, otherwise default to ascending
    const direction = (oldState.sortColumn === column || (column.id !== undefined && column.id === oldState.sortColumn!.id)) ? invert(oldState.sortDirection) : 'asc';

    return R.mergeDeepRight(
      oldState,
      {
        sortDirection: direction,
        sortColumn:    column,
        sortedData:    column.onSort!(direction === 'asc', oldState.sortedData || []),
      },
    );
  };


  public renderSortingArrow = (
    sortDirection: SortDirection,
    isVisible: boolean,
  ) => <img
    src={downArrow}
    alt={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
    css={sortArrowCss(isVisible, sortDirection)}
  />;


  public render = () => {
    const hasError = !isNilOrEmpty(this.props.error);
    const hasData  = !isNilOrEmpty(this.state.sortedData);
    const onTooltipClick = (event) => {
      event.preventDefault();
      event.stopPropagation(); // Prevent info button click from triggering column sort logic
    }

    return (
      <div
        css={css`transition: all .5s ease;
          ${this.props.rootCss};`}
      >
        <ErrorView boxStyle={this.props.errorBoxedStyle ? this.props.errorBoxedStyle : undefined} error={this.props.error} />
        {
          !hasError && <table css={tableStyle}>
            <thead>
              <tr>
                { // Column Headers with Sort Handling
                  this.props.columns.map(column => (
                    <th
                      key={column.id}
                      onClick={this.onSort(column)}
                      scope='col'
                      css={css`
                        cursor: ${column.onSort ? 'pointer' : 'auto'};
                        ${column.headerCss};
                        ${UNSELECTABLE_TEXT};
                        white-space: nowrap;
                        max-width: 200px;
                      `}
                      title={column.tooltipText}
                    >
                      <div css={css`display: flex; flex: 1 1 auto; min-width: 0;`}>
                      <span css={css`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`}>{column.title}</span>
                      {this.renderSortingArrow(this.state.sortDirection, this.state.sortColumn === column || (column.id !== undefined && this.state.sortColumn?.id === column.id))}
                      {this.props.infoKey && column.title === this.props.infoKey && 
                        <TooltipFC
                          content={<span css={css`white-space: pre-wrap`}>{this.props.infoMsg}</span>}
                          children={<img alt='ⓘ' src={infoImg} css={infoStyle} onClick={onTooltipClick} />}/>}
                      </div>
                    </th>
                  ))
                }
              </tr>
            </thead>
            <tbody>
              { // Loading Data
                this.props.isLoading && <tr css={css`background-color: transparent !important;`}>
                  <td colSpan={this.props.columns.length}>
                    <Loader
                      rootCss={css`
                        height: 120px;
                        width: 120px;
                        margin: 50px auto;
                      `}
                    />
                  </td>
                </tr>
              }
              { // No Data
                !hasData && !this.props.isLoading && <tr>
                  <td
                    colSpan={this.props.columns.length}
                    css={css`
                      text-align: center;
                      font-style: italic;
                    `}
                  >{this.props.noDataMessage}</td>
                </tr>
              }
              { // Data
                !this.props.isLoading && hasData && this.state.sortedData.map(this.props.renderRow)
              }
            </tbody>
          </table>
        }
      </div>
    );
  };
}
